import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { InventoryOutputModel } from 'src/app/models/inventory/inventory-output.model';
import { SalesRemissionPrintModel } from 'src/app/models/print/inventory/sales-remission-print.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryOutputTableModel } from 'src/app/models/tables/inventory/inventory-output-table.model';
import { SalesRemissionSelectionTableModel } from 'src/app/models/tables/inventory/sales-remission-selection-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SalesRemissionService {

  constructor(private _httpClient: HttpClient) { }

  blockSalesRemission(inventoryOutputId: number): Observable<InventoryOutputModel> {
    return this._httpClient.put<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.BlockSalesRemissionAsync}/${inventoryOutputId}`,
      null);
  }

  createSalesRemission(inventoryOutputContract: InventoryOutputModel): Observable<InventoryOutputModel> {
    return this._httpClient.post<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.CreateSalesRemission}`,
      inventoryOutputContract);
  }

  deleteSalesRemission(inventoryOutputId: number): Observable<InventoryOutputModel> {
    return this._httpClient.delete<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.DeleteSalesRemission}/${inventoryOutputId}`);
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  getAllClientsForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetAllClientsForSelectAsync}`);
  }

  getAllCostCenterForSelectAsync(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetAllCostCenterForSelectAsync}`,
      { params }
    );
  }

  getAllInventoriesForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetAllInventoriesForSelectAsync}`,);
  }

  getAllInventoryOutputTypesForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetAllInventoryOutputTypesForSelectAsync}`);
  }

  getAllSalesRemissionsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<InventoryOutputTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<InventoryOutputTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetAllSalesRemissionsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllTransporterForSelect(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetAllTransporterForSelectAsync}`,
      { params }
    );
  }

  getAllVehicleForSelect(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetAllVehicleForSelectAsync}`,
      { params }
    );
  }

  getClientForSelect(clientId: number): Observable<any> {
    const params = new HttpParams().set('clientId', clientId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetClientForSelectAsync}`,
      { params }
    );
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetColumnsForGeneralConsultationAsync}`);
  }

  getColumnsForSalesRemissionSelection(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetColumnsForSalesRemissionSelectionAsync}`);
  }

  getConceptForSaleAsync(): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetConceptForSaleAsync}`);
  }

  getCostCenterForSelect(costCenterId: number): Observable<any> {
    const params = new HttpParams().set('costCenterId', costCenterId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetCostCenterForSelectAsync}`,
      { params }
    );
  }

  getEmptySalesRemissionPrint(): Observable<SalesRemissionPrintModel> {
    return this._httpClient.get<SalesRemissionPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetEmptySalesRemissionPrint}`);
  }

  getFirstSalesRemissionByKeysAsync(inventoryId: number, branchId: number): Observable<InventoryOutputModel> {
    const params = new HttpParams().set('inventoryId', inventoryId).set('branchId', branchId);
    return this._httpClient.get<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetFirstSalesRemissionByKeysAsync}`,
      { params }
    );
  }

  getLastSalesRemissionByKeysAsync(inventoryId: number, branchId: number): Observable<InventoryOutputModel> {
    const params = new HttpParams().set('inventoryId', inventoryId).set('branchId', branchId);
    return this._httpClient.get<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetLastSalesRemissionByKeysAsync}`,
      { params }
    );
  }

  getNextSalesRemissionByKeysAsync(inventoryId: number, branchId: number, consecutive: number): Observable<InventoryOutputModel> {
    const params = new HttpParams().set('inventoryId', inventoryId).set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetNextSalesRemissionByKeysAsync}`,
      { params });
  }

  getPermissionPrint(): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetPermissionPrint}`);
  }

  getPreviusSalesRemissionByKeysAsync(inventoryId: number, branchId: number, consecutive: number): Observable<InventoryOutputModel> {
    const params = new HttpParams().set('inventoryId', inventoryId).set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetPreviusSalesRemissionByKeysAsync}`,
      { params });
  }

  getSalesRemissionById(inventoryOutputId: number): Observable<InventoryOutputModel> {
    return this._httpClient.get<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetSalesRemissionById}/${inventoryOutputId}`);
  }

  getSalesRemissionByKeysAsync(inventoryId: number, branchId: number, consecutive: number): Observable<InventoryOutputModel> {
    const params = new HttpParams().set('inventoryId', inventoryId).set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetSalesRemissionByKeysAsync}`,
      { params }
    );
  }

  getSalesRemissionByKeysForPrintAsync(inventoryOutputId: number, inventoryId: number, branchId: number, consecutive: number): Observable<SalesRemissionPrintModel> {
    const params = new HttpParams().set('inventoryOutputId', inventoryOutputId).set('inventoryId', inventoryId).set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<SalesRemissionPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetSalesRemissionByKeysForPrintAsync}`,
      { params }
    );
  }

  getSalesRemissionRelatedConsecutives(inventoryOutputId: number): Observable<any> {
    const params = new HttpParams().set('inventoryOutputId', inventoryOutputId);
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetSalesRemissionRelatedConsecutivesAsync}`,
      { params }
    );
  }

  getSalesRemissionsByInvoiceId(invoiceId: number): Observable<boolean> {
    const params = new HttpParams().set('invoiceId', invoiceId);
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetSalesRemissionsByInvoiceIdAsync}`,
      { params }
    );
  }

  getSalesRemissionsForSelection(settlementType: number, clientId: number, page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<SalesRemissionSelectionTableModel>> {
    const params = new HttpParams().set('settlementType', settlementType).set('clientId', clientId).set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<SalesRemissionSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.GetSalesRemissionsForSelectionAsync}`,
      filters, { params: params }
    );
  }

  setSalesRemissionState(inventoryOutputId: number): Observable<InventoryOutputModel> {
    return this._httpClient.put<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.SetSalesRemissionStateAsync}/${inventoryOutputId}`,
      null);
  }

  updateSalesRemission(inventoryOutputContract: InventoryOutputModel): Observable<InventoryOutputModel> {
    return this._httpClient.put<InventoryOutputModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.salesRemission.UpdateSalesRemission}`,
      inventoryOutputContract);
  }
}
